import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Button, Card, Chip, Divider, Stack, Typography } from '@mui/material';
// utils
import { fDate } from '../../../utils/formatTime';
// components
import Iconify from '../../../components/iconify';
import { MotionInView, varFadeInUp } from '../../../components/animate';
// utils
import { DEFAULT_BLOG_COVER } from '../../../utils/blog';

// ----------------------------------------------------------------------

// Compact horizontal item: a small thumbnail plus two lines of title and the
// meta a reader needs to judge relevance before clicking.
function RelatedItem({ post, isFirst }) {
  const { slug, cover, title, category, readTime, createdAt } = post ?? {};

  return (
    <Box
      component={RouterLink}
      to={`/blog/${slug}`}
      sx={{
        display: 'flex',
        gap: 1.5,
        py: 1.75,
        borderTop: isFirst ? 'none' : (theme) => `1px solid ${theme.palette.divider}`,
        textDecoration: 'none',
        color: 'inherit',
        '@media (hover: hover)': {
          '&:hover': {
            '& img': { transform: 'scale(1.08)' },
            '& .related-title': { color: 'primary.main' },
          },
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          flexShrink: 0,
          width: { xs: 84, sm: 96 },
          aspectRatio: '3 / 2',
          borderRadius: 1.5,
          overflow: 'hidden',
          bgcolor: 'grey.200',
          '& img': {
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.5s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
          },
        }}
      >
        <Box component="img" src={cover} alt={title} loading="lazy" decoding="async" onError={(event) => { event.currentTarget.src = DEFAULT_BLOG_COVER; }} />
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          className="related-title"
          variant="body2"
          sx={{
            color: 'text.primary',
            fontWeight: 600,
            lineHeight: 1.45,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            transition: 'color 0.2s ease',
          }}
        >
          {title}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mt: 0.75, alignItems: 'center' }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: 'primary.main',
            }}
          >
            {category}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {readTime} min
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {fDate(createdAt)}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}

RelatedItem.propTypes = {
  post: PropTypes.object.isRequired,
  isFirst: PropTypes.bool,
};

// ----------------------------------------------------------------------

// Sidebar block (1/4 of the row on desktop) that keeps the reader moving:
// related articles matched on category, a browse-all link and a single
// conversion action.
export default function RelatedBlogsWidget({ posts, category, title = 'Related reading' }) {
  return (
    <MotionInView variants={varFadeInUp}>
      <Card sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" sx={{ color: 'text.primary' }}>
            {title}
          </Typography>
          <Chip
            label={category}
            size="small"
            sx={{
              height: 22,
              fontSize: 10.5,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
            }}
          />
        </Stack>

        <Divider sx={{ mt: 1.5 }} />

        {posts.length > 0 ? (
          <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
            {posts.map((post, index) => (
              <li key={post.slug}>
                <RelatedItem post={post} isFirst={index === 0} />
              </li>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" sx={{ mt: 2, color: 'text.disabled' }}>
            No related articles yet.
          </Typography>
        )}

        <Button
          component={RouterLink}
          to="/blogs"
          fullWidth
          variant="outlined"
          endIcon={<Iconify icon="eva:arrow-forward-outline" />}
          sx={{ mt: 2 }}
        >
          Browse all articles
        </Button>
      </Card>

      <Card
        sx={{
          mt: 2.5,
          p: { xs: 2, sm: 2.5 },
          color: 'common.white',
          bgcolor: (theme) => alpha(theme.palette.grey[900], 0.94),
          backgroundImage: (theme) =>
            `radial-gradient(120% 120% at 100% 0%, ${alpha(theme.palette.primary.main, 0.5)} 0%, ${alpha(
              theme.palette.grey[900],
              0
            )} 60%)`,
        }}
      >
        <Typography variant="subtitle2" sx={{ color: 'common.white', mb: 0.75 }}>
          Not sure which exam applies to you?
        </Typography>

        <Typography variant="body2" sx={{ color: (theme) => alpha(theme.palette.common.white, 0.72), mb: 2 }}>
          Get a free eligibility check and a realistic timeline for DHA, MOH or HAAD.
        </Typography>

        <Button
          component={RouterLink}
          to="/contact-us"
          fullWidth
          variant="contained"
          sx={{ bgcolor: 'common.white', color: 'grey.900', '&:hover': { bgcolor: 'grey.200' } }}
        >
          Talk to a consultant
        </Button>
      </Card>
    </MotionInView>
  );
}

RelatedBlogsWidget.propTypes = {
  posts: PropTypes.array.isRequired,
  category: PropTypes.string,
  title: PropTypes.string,
};
